import axios from 'axios';
import i18n from './i18n';

// Pobranie API_URL z env lub ustawienie domyślnego
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const instance = axios.create({
    baseURL: API_BASE_URL, // Dynamicznie ustawiana baza URL
});

// Track ongoing refresh to prevent concurrent refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

// Request Interceptor: Dodanie JWT tokena tylko do odpowiednich żądań
instance.interceptors.request.use(
    (config) => {
        // Wykluczamy endpointy logowania, rejestracji trenera, tworzenia szkoły oraz dodawania godzin otwarcia
        const excludedEndpoints = [
            '/api/v1/auth/login',
            '/api/v1/auth/refresh',
            '/api/v1/auth/register/trainer',
            '/api/v1/auth/register/owner',
            '/api/v1/auth/registerToExistingSchool/trainer',
            '/api/v1/auth/confirm',
            '/api/v1/auth/reset-password',
            '/api/v1/auth/reset-password-request',
            '/api/v1/school/create', // obejmuje np. /api/v1/school/create/{trainerId}
            '/opening-hours'         // obejmuje np. /api/v1/school/{schoolId}/opening-hours
        ];

        if (!excludedEndpoints.some((endpoint) => config.url.includes(endpoint))) {
            const token = localStorage.getItem('jwt');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response &&
            error.response.status === 401 &&
            error.response.data?.message === "JWT token is expired" &&
            !originalRequest._retry
        ) {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                // No refresh token — force logout
                localStorage.setItem("sessionExpiredMessage", i18n.t('common:sessionExpired'));
                localStorage.removeItem("jwt");
                localStorage.removeItem("refreshToken");
                window.location.href = "/";
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Another request is already refreshing — queue this one
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(instance(originalRequest));
                        },
                        reject,
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
                    refreshToken,
                });

                const { jwt: newToken, refreshToken: newRefreshToken } = response.data;

                localStorage.setItem('jwt', newToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                processQueue(null, newToken);

                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return instance(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);

                localStorage.setItem("sessionExpiredMessage", i18n.t('common:sessionExpired'));
                localStorage.removeItem("jwt");
                localStorage.removeItem("refreshToken");
                window.location.href = "/";
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);


export default instance;
